import React from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Snackbar,
  Alert,
} from "@mui/material";
import { contentWrite, createPreviewPR } from "../../services/content";
import PublishBar from "../../components/admin/PublishBar";

type Company = {
  phone?: string;
  email?: string;
  address?: { line1?: string; line2?: string };
  workingHours?: string;
  social?: {
    whatsapp?: string;
    instagram?: string;
    facebook?: string;
    linkedin?: string;
  };
};

const CompanyAdmin: React.FC = () => {
  const [company, setCompany] = React.useState<Company>({
    phone: "",
    email: "",
    address: { line1: "", line2: "" },
    workingHours: "",
    social: { whatsapp: "", instagram: "", facebook: "", linkedin: "" },
  });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [publishMode, setPublishMode] = React.useState<"prod" | "preview">(
    "prod"
  );
  const [commitMsg, setCommitMsg] = React.useState("chore(company): update");
  const [branchName, setBranchName] = React.useState("");
  const [prUrl, setPrUrl] = React.useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await fetch("/data/company/index.json", { cache: "no-store" });
      const json = await r.json();
      setCompany(json || {});
    } catch (e: any) {
      // keep defaults
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setLoading(true);
    setError("");
    try {
      if (!company.email?.trim()) throw new Error("E-posta gerekli");
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(company.email))
        throw new Error("Geçersiz e-posta");
      if (
        publishMode === "preview" &&
        branchName &&
        !/^[a-zA-Z0-9/_-]+$/.test(branchName)
      ) {
        throw new Error("Geçersiz branch adı");
      }

      const branch =
        publishMode === "preview"
          ? branchName || `preview/company-${Date.now()}`
          : undefined;

      await contentWrite(
        "data/company/index.json",
        JSON.stringify(company, null, 2),
        commitMsg,
        branch
      );

      if (branch) {
        const pr = await createPreviewPR(branch, "Company Info Update");
        setPrUrl(pr.url);
      }

      setSuccess("Kaydedildi");
    } catch (e: any) {
      setError(e?.message || "Kaydedilemedi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="md">
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 3 }}>
          Firma Bilgileri
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          İpucu: Firma iletişim ve sosyal hesaplar buradan güncellenir. Yayın
          çubuğu ile Prod veya Preview PR seçebilirsiniz.
        </Alert>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <PublishBar
            commitMsg={commitMsg}
            setCommitMsg={setCommitMsg}
            publishMode={publishMode}
            setPublishMode={setPublishMode}
            branchName={branchName}
            setBranchName={setBranchName}
            branchPlaceholder={`preview/company`}
          />
          <Button
            variant="contained"
            onClick={save}
            disabled={loading}
          >
            Değişiklikleri Yayına Al
          </Button>
        </Stack>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Telefon"
                  fullWidth
                  value={company.phone || ""}
                  onChange={(e) =>
                    setCompany((c) => ({ ...c, phone: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="E-posta"
                  fullWidth
                  value={company.email || ""}
                  onChange={(e) =>
                    setCompany((c) => ({ ...c, email: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Adres Satır 1"
                  fullWidth
                  value={company.address?.line1 || ""}
                  onChange={(e) =>
                    setCompany((c) => ({
                      ...c,
                      address: { ...(c.address || {}), line1: e.target.value },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Adres Satır 2"
                  fullWidth
                  value={company.address?.line2 || ""}
                  onChange={(e) =>
                    setCompany((c) => ({
                      ...c,
                      address: { ...(c.address || {}), line2: e.target.value },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Çalışma Saatleri"
                  fullWidth
                  value={company.workingHours || ""}
                  onChange={(e) =>
                    setCompany((c) => ({ ...c, workingHours: e.target.value }))
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="WhatsApp"
                  fullWidth
                  value={company.social?.whatsapp || ""}
                  onChange={(e) =>
                    setCompany((c) => ({
                      ...c,
                      social: { ...(c.social || {}), whatsapp: e.target.value },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Instagram"
                  fullWidth
                  value={company.social?.instagram || ""}
                  onChange={(e) =>
                    setCompany((c) => ({
                      ...c,
                      social: {
                        ...(c.social || {}),
                        instagram: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Facebook"
                  fullWidth
                  value={company.social?.facebook || ""}
                  onChange={(e) =>
                    setCompany((c) => ({
                      ...c,
                      social: { ...(c.social || {}), facebook: e.target.value },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="LinkedIn"
                  fullWidth
                  value={company.social?.linkedin || ""}
                  onChange={(e) =>
                    setCompany((c) => ({
                      ...c,
                      social: { ...(c.social || {}), linkedin: e.target.value },
                    }))
                  }
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <Button variant="outlined" onClick={load} disabled={loading}>
                Yenile
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Snackbar
          open={!!error}
          onClose={() => setError("")}
          autoHideDuration={5000}
        >
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        </Snackbar>
        <Snackbar
          open={!!success}
          onClose={() => setSuccess("")}
          autoHideDuration={2500}
        >
          <Alert severity="success" onClose={() => setSuccess("")}>
            {success}
            {prUrl && (
              <Button
                component="a"
                href={prUrl}
                target="_blank"
                size="small"
                sx={{ ml: 1 }}
              >
                PR'ı Aç
              </Button>
            )}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default CompanyAdmin;
